/** Settings → Run credentials tab: EMR and platform tokens, listed by name and hint. Values never come back. */

import { Plus } from "lucide-react";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { EmptyState, QueryState, errorText } from "@/components/ui/states";
import { useHolds } from "@/components/use-holds";
import { useCurrentUser } from "@/features/auth/hooks";
import { useCredentials, useDeleteCredential } from "@/features/credentials/hooks";
import type { Credential } from "@/features/credentials/types";
import { formatRelative } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { AddCredentialDialog } from "./add-credential-dialog";
import { DeleteButton } from "./delete-button";
import { useAddParam } from "./use-add-param";

function CredentialRow({ credential: c }: { credential: Credential }) {
  const remove = useDeleteCredential();
  const notify = useUiStore((s) => s.notify);
  // Shared by every project: its creator removes it, or a workbench admin.
  const me = useCurrentUser().data?.email.toLowerCase();
  const mayDelete = useHolds("credentials:manage") || c.created_by.toLowerCase() === me;
  const del = () =>
    remove.mutate(c.id, {
      onSuccess: () => notify(`Deleted ${c.name}.`),
      onError: (e) => notify(errorText(e, "Could not delete the credential."), "error"),
    });
  return (
    <tr>
      <td>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-semibold">{c.name}</span>
        </div>
      </td>
      <td className="font-mono text-[13px]">{c.hint}</td>
      <td className="text-muted">
        {c.created_by} · {formatRelative(c.created_at)}
      </td>
      <td>
        <div className="flex justify-end">
          {mayDelete && <DeleteButton what={c.name} pending={remove.isPending} onDelete={del} />}
        </div>
      </td>
    </tr>
  );
}

export function CredentialsTab() {
  const credentials = useCredentials();
  const [adding, setAdding] = useAddParam();
  const addButton = (
    <Button variant="primary" onClick={() => setAdding(true)}>
      <Plus size={14} strokeWidth={2.2} aria-hidden />
      Add credential
    </Button>
  );
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-muted">
          Tokens an agent needs inside a run, such as an EMR token. Experiments refer to them by name.
        </p>
        {addButton}
      </div>
      <Box>
        <QueryState query={credentials} what="credentials">
          {credentials.data?.length ? (
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Value</th>
                    <th>Added</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {credentials.data.map((c) => (
                    <CredentialRow key={c.id} credential={c} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No run credentials" action={addButton}>
              Add an EMR or platform token once and pick it by name when you set up an experiment.
            </EmptyState>
          )}
        </QueryState>
      </Box>
      {adding && <AddCredentialDialog onClose={() => setAdding(false)} />}
    </>
  );
}
